import { supabase } from '@/integrations/supabase/client';

export type CtaType = 'webinar' | 'check' | 'waitlist' | 'whatsapp' | 'apply' | 'info';

export interface KPConfig {
  id: string;
  version: number;
  updated_at: string;
  // Scoring thresholds
  low_max: number;
  medium_max: number;
  high_min: number;
  // CTA mapping
  cta_low_type: CtaType;
  cta_medium_type: CtaType;
  cta_high_type: CtaType;
  // Routing
  route_low_to: string;
  route_medium_to: string;
  route_high_to: string;
  // Time overrides
  sunday_cta_type: CtaType;
  night_start_hour: number;
  night_end_hour: number;
  night_cta_type: CtaType;
  // Source overrides
  source_podcast_cta_type: CtaType;
  source_eo_cta_type: CtaType;
  source_linkedin_cta_type: CtaType;
}

interface CachedConfig {
  config: KPConfig;
  timestamp: number;
}

const CACHE_KEY = 'kp_config_cache';
const CACHE_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

// Default config as fallback
const DEFAULT_CONFIG: KPConfig = {
  id: 'default',
  version: 0,
  updated_at: new Date().toISOString(),
  low_max: 2,
  medium_max: 6,
  high_min: 7,
  cta_low_type: 'webinar',
  cta_medium_type: 'check',
  cta_high_type: 'apply',
  route_low_to: '',
  route_medium_to: '',
  route_high_to: '',
  sunday_cta_type: 'apply',
  night_start_hour: 22,
  night_end_hour: 6,
  night_cta_type: 'waitlist',
  source_podcast_cta_type: 'apply',
  source_eo_cta_type: 'apply',
  source_linkedin_cta_type: 'check',
};

// Extend Window interface
declare global {
  interface Window {
    kp_config: Readonly<KPConfig>;
  }
}

/**
 * Load cached config from localStorage
 */
function loadFromCache(): KPConfig | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    const parsed: CachedConfig = JSON.parse(cached);
    const age = Date.now() - parsed.timestamp;
    
    // Return cached config even if stale (will be updated in background)
    if (parsed.config && typeof parsed.config === 'object') {
      return parsed.config;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Save config to localStorage cache
 */
function saveToCache(config: KPConfig): void {
  try {
    const cached: CachedConfig = {
      config,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

/**
 * Fetch config from backend
 */
async function fetchFromBackend(): Promise<KPConfig | null> {
  try {
    const { data, error } = await supabase
      .from('intelligence_config')
      .select('*')
      .limit(1)
      .maybeSingle();
    
    if (error) {
      console.warn('[KPConfig] Backend fetch error:', error.message);
      return null;
    }
    
    return data as KPConfig | null;
  } catch (err) {
    console.warn('[KPConfig] Backend fetch failed:', err);
    return null;
  }
}

/**
 * Initialize the global config
 * Call this once at app startup
 */
export async function initKPConfig(): Promise<KPConfig> {
  // Try to load from cache first (for immediate availability)
  const cachedConfig = loadFromCache();
  
  // Set initial config (cache or default)
  let activeConfig = cachedConfig || DEFAULT_CONFIG;
  window.kp_config = Object.freeze({ ...activeConfig });
  
  // Fetch fresh config from backend
  const backendConfig = await fetchFromBackend();
  
  if (backendConfig) {
    // Update with fresh config
    activeConfig = backendConfig;
    window.kp_config = Object.freeze({ ...activeConfig });
    saveToCache(activeConfig);
    console.log('[KPConfig] Loaded from backend, version:', backendConfig.version);
  } else if (cachedConfig) {
    console.log('[KPConfig] Using cached config, version:', cachedConfig.version);
  } else {
    console.log('[KPConfig] Using default config');
  }
  
  return activeConfig;
}

/**
 * Get the current active config
 * Always returns a valid config (never null)
 */
export function getConfig(): Readonly<KPConfig> {
  // Return from window if available
  if (window.kp_config) {
    return window.kp_config;
  }
  
  // Try cache as fallback
  const cached = loadFromCache();
  if (cached) {
    window.kp_config = Object.freeze({ ...cached });
    return window.kp_config;
  }
  
  // Return default as last resort
  window.kp_config = Object.freeze({ ...DEFAULT_CONFIG });
  return window.kp_config;
}

/**
 * Force refresh config from backend
 * Useful after admin updates
 */
export async function refreshConfig(): Promise<KPConfig> {
  const backendConfig = await fetchFromBackend();
  
  if (backendConfig) {
    window.kp_config = Object.freeze({ ...backendConfig });
    saveToCache(backendConfig);
    return backendConfig;
  }
  
  return getConfig();
}

/**
 * Check if current hour is within night hours
 */
export function isNightTime(config: KPConfig = getConfig()): boolean {
  const hour = new Date().getHours();
  
  if (config.night_start_hour > config.night_end_hour) {
    // Night spans midnight (e.g., 22-6)
    return hour >= config.night_start_hour || hour < config.night_end_hour;
  }
  // Night within same day
  return hour >= config.night_start_hour && hour < config.night_end_hour;
}

/**
 * Check if today is Sunday
 */
export function isSunday(): boolean {
  return new Date().getDay() === 0;
}

/**
 * Get the appropriate CTA type based on score and overrides
 */
export function getCtaTypeForScore(
  score: number,
  options?: {
    source?: string;
    forceNightCheck?: boolean;
    forceSundayCheck?: boolean;
  }
): CtaType {
  const config = getConfig();
  const { source, forceNightCheck = true, forceSundayCheck = true } = options || {};
  
  // Source overrides (highest priority)
  if (source) {
    const sourceLower = source.toLowerCase();
    if (sourceLower.includes('podcast')) return config.source_podcast_cta_type;
    if (sourceLower.includes('eo') || sourceLower === 'eo') return config.source_eo_cta_type;
    if (sourceLower.includes('linkedin')) return config.source_linkedin_cta_type;
  }
  
  // Sunday override
  if (forceSundayCheck && isSunday()) {
    return config.sunday_cta_type;
  }
  
  // Night override
  if (forceNightCheck && isNightTime(config)) {
    return config.night_cta_type;
  }
  
  // Score-based CTA
  if (score <= config.low_max) return config.cta_low_type;
  if (score <= config.medium_max) return config.cta_medium_type;
  return config.cta_high_type;
}

/**
 * Get the route for a given score
 */
export function getRouteForScore(score: number): string {
  const config = getConfig();
  
  if (score <= config.low_max) return config.route_low_to;
  if (score <= config.medium_max) return config.route_medium_to;
  return config.route_high_to;
}
